import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL;

// Study with Caleb: a quiet focus timer where he sits with you the whole time.
// While you focus, his stickers slowly take turns. He speaks up at a few
// moments (your own lines, set in study settings), notices if you leave, and
// celebrates when a session ends. Detailed numbers live on the statistics
// page; gifts he's given you live in From Caleb.

// built-in stickers, used for any moment you haven't uploaded your own for
const DEFAULT_STICKERS = {
  study: ['/stickers/night.webp', '/stickers/idea.webp', '/stickers/peek.webp'],
  done: ['/stickers/pat.webp'],
  break: ['/stickers/feed.webp'],
  back: ['/stickers/grumpy.webp'],
};
const FALLBACK = '/stickers/night.webp';

const FOCUS_OPTIONS = [{ min: 30, label: '30m' }, { min: 60, label: '1h' }, { min: 120, label: '2h' }];
const BREAK_OPTIONS = [5, 10, 15];
const SAVE_KEY = 'studyTimer';
const ROTATE_MS = 60000;          // a different sticker every minute
const FADE_OUT_MS = 1400;         // the current one slowly fades away…
const FADE_IN_MS = 1800;          // …then the next one slowly appears

const pick = (arr) => (arr && arr.length ? arr[Math.floor(Math.random() * arr.length)] : '');

const fmt = (ms) => {
  const total = Math.max(0, Math.round(ms / 1000));
  const h = Math.floor(total / 3600);
  const m = Math.floor((total % 3600) / 60);
  const s = total % 60;
  return h ? `${h}:${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}` : `${m}:${String(s).padStart(2, '0')}`;
};
const hm = (min) => {
  const h = Math.floor((min || 0) / 60);
  const m = (min || 0) % 60;
  if (!h) return `${m}m`;
  return m ? `${h}h ${m}m` : `${h}h`;
};
// reading time: "2h 15m" from seconds. Rounded UP, like a countdown clock:
// 2h 19m stays 2h 19m until a full minute has been read, then shows 2h 18m
const hmSec = (sec) => {
  const s0 = Math.max(0, Math.round(sec || 0));
  if (s0 > 0 && s0 < 60) return 'under a minute';
  return hm(Math.ceil(s0 / 60));
};
// while you're reading, the bank counts down to the second: "2h 18m 59s"
const hmsLeft = (ms) => {
  const t = Math.max(0, Math.floor(ms / 1000));
  const h = Math.floor(t / 3600), m = Math.floor((t % 3600) / 60), sec = t % 60;
  return h ? `${h}h ${m}m ${sec}s` : m ? `${m}m ${sec}s` : `${sec}s`;
};
const clock = (ms) => {
  const total = Math.max(0, Math.floor(ms / 1000));
  const p = (n) => String(n).padStart(2, '0');
  return `${p(Math.floor(total / 3600))}:${p(Math.floor((total % 3600) / 60))}:${p(total % 60)}`;
};
// a subject's colour, and a darker shade of it for gradients
const darken = (hex, f = 0.68) => {
  const m = /^#([0-9a-f]{6})$/i.exec(hex || '');
  if (!m) return '#8a5f35';
  const n = parseInt(m[1], 16);
  const c = [(n >> 16) & 255, (n >> 8) & 255, n & 255].map(v => Math.round(v * f));
  return `#${c.map(v => v.toString(16).padStart(2, '0')).join('')}`;
};
const alpha = (hex, a) => {
  const m = /^#([0-9a-f]{6})$/i.exec(hex || '');
  if (!m) return `rgba(201,147,90,${a})`;
  const n = parseInt(m[1], 16);
  return `rgba(${(n >> 16) & 255},${(n >> 8) & 255},${n & 255},${a})`;
};
const load = (key, fallback) => {
  try { const v = localStorage.getItem(key); return v === null ? fallback : JSON.parse(v); } catch (e) { return fallback; }
};
const save = (key, value) => {
  try { if (value === null) localStorage.removeItem(key); else localStorage.setItem(key, JSON.stringify(value)); } catch (e) { /* fine */ }
};


// a soft two-note chime, no sound file needed
const chime = () => {
  try {
    const Ctx = window.AudioContext || window.webkitAudioContext;
    const ctx = new Ctx();
    [[880, 0], [1320, 0.18]].forEach(([freq, at]) => {
      const o = ctx.createOscillator();
      const g = ctx.createGain();
      o.type = 'sine';
      o.frequency.value = freq;
      g.gain.setValueAtTime(0.0001, ctx.currentTime + at);
      g.gain.exponentialRampToValueAtTime(0.18, ctx.currentTime + at + 0.02);
      g.gain.exponentialRampToValueAtTime(0.0001, ctx.currentTime + at + 0.9);
      o.connect(g).connect(ctx.destination);
      o.start(ctx.currentTime + at);
      o.stop(ctx.currentTime + at + 1);
    });
    setTimeout(() => ctx.close(), 1500);
  } catch (e) { /* no audio, no problem */ }
};

// small line icons, same weight as the rest of the site
const HeartIcon = ({ size = 14 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="#c9935a" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M12 20.5s-7.5-4.6-9.2-9.3C1.6 7.8 3.8 4.5 7.2 4.5c2 0 3.5 1.1 4.8 2.8 1.3-1.7 2.8-2.8 4.8-2.8 3.4 0 5.6 3.3 4.4 6.7-1.7 4.7-9.2 9.3-9.2 9.3z" />
  </svg>
);

const GiftIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#c9935a" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <rect x="3.5" y="8.5" width="17" height="4" rx="1" />
    <path d="M5 12.5v7a1 1 0 0 0 1 1h12a1 1 0 0 0 1-1v-7" />
    <path d="M12 8.5v12" />
    <path d="M12 8.5c-1.8-3.4-5.4-4-5.4-1.6 0 1.4 2.2 1.6 5.4 1.6z" />
    <path d="M12 8.5c1.8-3.4 5.4-4 5.4-1.6 0 1.4-2.2 1.6-5.4 1.6z" />
  </svg>
);

const GearIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#c9935a" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <circle cx="12" cy="12" r="3" />
    <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 1 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-4 0v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 1 1-2.83-2.83l.06-.06A1.65 1.65 0 0 0 4.68 15a1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1 0-4h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 1 1 2.83-2.83l.06.06A1.65 1.65 0 0 0 9 4.68a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 4 0v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 1 1 2.83 2.83l-.06.06A1.65 1.65 0 0 0 19.32 9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1z" />
  </svg>
);

const BookIcon = ({ size = 15 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M12 6.5C10.3 5.2 7.8 4.5 4 4.5v13c3.8 0 6.3.7 8 2 1.7-1.3 4.2-2 8-2v-13c-3.8 0-6.3.7-8 2z" />
    <path d="M12 6.5v13" />
  </svg>
);

export default function Study() {
  const navigate = useNavigate();
  const token = localStorage.getItem('token');

  // timer: { phase: 'focus'|'break', planned, endAt, remaining (when paused), startedAt, timesLeft, cues }
  const [timer, setTimer] = useState(() => load(SAVE_KEY, null));
  const [now, setNow] = useState(Date.now());
  const [focusLen, setFocusLen] = useState(() => {
    const saved = load('studyFocusLen', 30);
    return FOCUS_OPTIONS.some(o => o.min === saved) ? saved : 30;
  });
  const [breakLen, setBreakLen] = useState(() => load('studyBreakLen', 5));
  // what you're studying: each subject earns reading time at its own rate
  const [subjects, setSubjects] = useState([]);
  const [subjectKey, setSubjectKey] = useState(() => load('studySubject', ''));
  const [stats, setStats] = useState(null);
  const [lines, setLines] = useState({});            // category -> lines to use
  const [stickers, setStickers] = useState(DEFAULT_STICKERS);
  const [bubble, setBubble] = useState('');
  const [momentSticker, setMomentSticker] = useState(null);   // a one-off sticker (done / back)
  const [rotateIdx, setRotateIdx] = useState(0);
  const [celebrate, setCelebrate] = useState(0);
  const [confirmStop, setConfirmStop] = useState(false);
  const [giftNotice, setGiftNotice] = useState(false);   // 'something from Caleb is waiting'
  // reading time: what the server says, plus how far its clock is from this device's
  const [reading, setReading] = useState(null);          // { visible, balance_seconds, active, offset }
  const [readBusy, setReadBusy] = useState(false);
  const [readNote, setReadNote] = useState('');          // '+45 min reading time 🌿'
  const readNoteTimer = useRef(null);
  const readStoppingRef = useRef(false);
  const [readOpen, setReadOpen] = useState(false);       // the reading timer, unfolded under the bank

  const wakeRef = useRef(null);
  const leftAtRef = useRef(null);
  const bubbleTimer = useRef(null);
  const momentTimer = useRef(null);
  const finishingRef = useRef(false);

  useEffect(() => {
    if (!token) { navigate('/'); return; }
    loadStats();
    loadReading();
    axios.get(`${API}/study/subjects?token=${token}`)
      .then(res => setSubjects(res.data.subjects || []))
      .catch(() => {});
    axios.get(`${API}/study/lines?token=${token}`)
      .then(res => {
        const map = {};
        (res.data.categories || []).forEach(c => { map[c.key] = c.custom.length ? c.custom : c.defaults; });
        setLines(map);
      })
      .catch(() => {});
    axios.get(`${API}/study/stickers?token=${token}`)
      .then(res => {
        const mine = {};
        // only stickers switched on for the rotation (hidden ones — and Caleb's
        // keepsakes you haven't added — stay out, without being deleted)
        (res.data.stickers || []).forEach(st => {
          if (st.show_in_rotation === false) return;
          (mine[st.moment] = mine[st.moment] || []).push(st.url);
        });
        setStickers({
          study: mine.study?.length ? mine.study : DEFAULT_STICKERS.study,
          done: mine.done?.length ? mine.done : DEFAULT_STICKERS.done,
          break: mine.break?.length ? mine.break : DEFAULT_STICKERS.break,
          back: mine.back?.length ? mine.back : DEFAULT_STICKERS.back,
        });
      })
      .catch(() => {});
  }, []);

  useEffect(() => { save(SAVE_KEY, timer); }, [timer]);

  // tick
  useEffect(() => {
    const id = setInterval(() => setNow(Date.now()), 500);
    return () => clearInterval(id);
  }, []);

  const running = timer && timer.endAt && timer.remaining == null;
  const remaining = !timer ? 0 : timer.remaining != null ? timer.remaining : Math.max(0, timer.endAt - now);
  const totalMs = timer ? timer.planned * 60000 : focusLen * 60000;
  const shownSubjects = subjects.filter(x => !x.hidden);
  const subject = shownSubjects.find(x => x.key === subjectKey) || shownSubjects[0] || null;
  const timerSubject = timer && timer.subject ? (subjects.find(x => x.key === timer.subject) || subject) : subject;
  const pickSubject = (k) => { setSubjectKey(k); save('studySubject', k); };
  const progress = timer ? 1 - remaining / totalMs : 0;

  // reading: elapsed from the server's start time, never from a counter
  const readActive = !!(reading && reading.active);
  const readElapsedMs = readActive ? Math.max(0, now + reading.offset - Date.parse(reading.active.started_at)) : 0;
  const readBalanceMs = reading ? reading.balance_seconds * 1000 : 0;
  const readLeftMs = Math.max(0, readBalanceMs - readElapsedMs);
  const readSessionMs = Math.min(readElapsedMs, readBalanceMs);
  const readStartMs = readBalanceMs;                     // what you had when this session began
  const RR = 66;
  const RC = 2 * Math.PI * RR;
  useEffect(() => {
    // the balance never goes below zero: when it's used up, reading stops
    if (readActive && readLeftMs <= 0 && !readStoppingRef.current) stopReading('ran_out');
  }, [now, readActive]);

  // the calm rotation: a different sticker every ~30 seconds while focusing or on break
  const pool = timer?.phase === 'break' ? stickers.break : stickers.study;
  useEffect(() => {
    if (!running || pool.length < 2) return undefined;
    const id = setInterval(() => setRotateIdx(i => i + 1), ROTATE_MS);
    return () => clearInterval(id);
  }, [running, pool.length]);
  const baseSticker = pool[rotateIdx % pool.length] || FALLBACK;
  const shownSticker = momentSticker || baseSticker;

  // a gentle change: fade the current sticker out, wait until the next one has
  // loaded, then fade it in — never a sudden swap
  const [displayed, setDisplayed] = useState(shownSticker);
  const [visible, setVisible] = useState(true);
  useEffect(() => {
    if (shownSticker === displayed) return undefined;
    let cancelled = false;
    setVisible(false);
    const t = setTimeout(() => {
      const img = new Image();
      const show = () => { if (!cancelled) { setDisplayed(shownSticker); setVisible(true); } };
      img.onload = show;
      img.onerror = show;
      img.src = shownSticker;
    }, FADE_OUT_MS);
    return () => { cancelled = true; clearTimeout(t); };
  }, [shownSticker]);

  const loadStats = () => {
    axios.get(`${API}/study/stats?token=${token}`)
      .then(res => {
        setStats(res.data);
        const t = load(SAVE_KEY, null);
        const focusing = t && t.phase === 'focus';
        if ((res.data.gifts_unseen || []).length && !focusing) setGiftNotice(true);
      })
      .catch(() => {});
  };

  // ---- reading time ----
  // the server keeps the balance and when the running session started; this
  // page only works out the elapsed time from the clock, so a reload or a
  // sleeping iPad never loses anything
  const takeReading = (data) => {
    if (!data) return;
    const serverNow = Date.parse(data.server_now);
    setReading({
      visible: data.visible !== false,
      balance_seconds: data.balance_seconds || 0,
      active: data.active || null,
      offset: Number.isFinite(serverNow) ? serverNow - Date.now() : 0,
    });
    if (data.just_finished && data.just_finished.end_reason === 'ran_out' && data.visible !== false) {
      showReadNote('your reading time is all used up — study to earn more 🌿', 7000);
    }
  };
  const loadReading = () => {
    axios.get(`${API}/reading/state?token=${token}`).then(res => takeReading(res.data)).catch(() => {});
  };
  const showReadNote = (text, ms = 5500) => {
    setReadNote(text);
    clearTimeout(readNoteTimer.current);
    readNoteTimer.current = setTimeout(() => setReadNote(''), ms);
  };
  // what you're reading: a fic or novel you're reading, or something else
  const [readItems, setReadItems] = useState([]);
  const [readItem, setReadItem] = useState(() => load('readingItem', ''));   // "fanfic:id" | "novel:id" | ""
  const loadReadItems = () => axios.get(`${API}/reading/items?token=${token}`).then(res => setReadItems(res.data.items || [])).catch(() => {});
  useEffect(() => { loadReadItems(); }, []);
  const itemParts = (v) => { const [kind, id] = (v || '').split(':'); return { item_kind: kind || null, item_id: id || null }; };
  const activeItemValue = reading && reading.active && reading.active.item_kind ? `${reading.active.item_kind}:${reading.active.item_id}` : '';
  const pickReadItem = (v) => {
    setReadItem(v);
    save('readingItem', v);
    if (reading && reading.active) {
      axios.put(`${API}/reading/active-item`, { token, ...itemParts(v) }).then(res => takeReading(res.data)).catch(() => {});
    }
  };

  const startReading = () => {
    setReadBusy(true);
    readStoppingRef.current = false;
    axios.post(`${API}/reading/start`, { token, ...itemParts(readItems.some(x => `${x.kind}:${x.id}` === readItem) ? readItem : '') })
      .then(res => takeReading(res.data))
      .catch(err => {
        const d = err?.response?.data?.detail;
        showReadNote(typeof d === 'string' ? d : "couldn't start — try again");
        loadReading();
      })
      .finally(() => setReadBusy(false));
  };
  const stopReading = (reason = 'stopped') => {
    if (readStoppingRef.current) return Promise.resolve();
    readStoppingRef.current = true;
    setReadBusy(true);
    return axios.post(`${API}/reading/stop`, { token, reason })
      .then(res => {
        takeReading(res.data);
        const done = res.data.just_finished;
        if (done && reason === 'stopped' && done.duration_seconds >= 60) {
          showReadNote(`${hm(Math.floor(done.duration_seconds / 60))} of reading 📖`, 4500);
        }
      })
      .catch(() => loadReading())
      .finally(() => { setReadBusy(false); readStoppingRef.current = false; });
  };

  const say = (text, sticker, ms = 6000) => {
    if (!text) return;
    setBubble(text);
    clearTimeout(bubbleTimer.current);
    bubbleTimer.current = setTimeout(() => setBubble(''), ms);
    if (sticker) {
      setMomentSticker(sticker);
      clearTimeout(momentTimer.current);
      momentTimer.current = setTimeout(() => setMomentSticker(null), ms);
    }
  };
  const line = (key) => pick(lines[key]);


  // keep the screen on while focusing (where the browser allows it)
  const holdScreen = async (on) => {
    try {
      if (on && 'wakeLock' in navigator && !wakeRef.current) {
        wakeRef.current = await navigator.wakeLock.request('screen');
        wakeRef.current.addEventListener('release', () => { wakeRef.current = null; });
      } else if (!on && wakeRef.current) {
        await wakeRef.current.release();
        wakeRef.current = null;
      }
    } catch (e) { /* not supported — fine */ }
  };

  // ---- start / pause / stop ----
  const start = () => {
    save('studyFocusLen', focusLen);
    save('studyBreakLen', breakLen);
    // reading pauses for studying: the reading session so far is saved
    if (reading && reading.active) stopReading('study');
    finishingRef.current = false;
    setConfirmStop(false);
    setRotateIdx(Math.floor(Math.random() * 100));
    setTimer({
      phase: 'focus', planned: focusLen, endAt: Date.now() + focusLen * 60000, remaining: null,
      startedAt: new Date().toISOString(), timesLeft: 0, cues: {},
      subject: subject ? subject.key : null, noticeAway: subject ? subject.notice_away !== false : true,
    });
    say(line('start'));
    holdScreen(true);
  };

  // each pause is kept (start and end), so a calendar can show the session as it really happened
  const pause = () => setTimer(t => (t ? {
    ...t, remaining: Math.max(0, t.endAt - Date.now()),
    pauses: [...(t.pauses || []), { start: new Date().toISOString() }],
  } : t));
  const resume = () => setTimer(t => (t ? {
    ...t, endAt: Date.now() + t.remaining, remaining: null,
    pauses: (t.pauses || []).map((p, i, all) => (i === all.length - 1 && !p.end ? { ...p, end: new Date().toISOString() } : p)),
  } : t));

  const saveSession = (t, focusedMs, completed) => {
    const minutes = Math.floor(focusedMs / 60000);
    if (minutes < 1) return Promise.resolve(null);
    // the real end: when the timer ran out (even if the page was closed), or now if you stopped it
    const endedAt = completed && t.endAt ? new Date(Math.min(t.endAt, Date.now())).toISOString() : new Date().toISOString();
    const pauses = (t.pauses || []).map(p => ({ start: p.start, end: p.end || endedAt }));
    return axios.post(`${API}/study/sessions`, {
      token, planned_minutes: t.planned, focused_minutes: minutes, completed,
      times_left: t.timesLeft || 0, started_at: t.startedAt, subject: t.subject || null,
      ended_at: endedAt, pauses,
    }).then(res => {
      if (res.data.stats) setStats(res.data.stats);
      // reading time is earned either way; the little "+45 min" only shows when
      // the reading part of the page is switched on
      const r = res.data.reading;
      if (r) {
        takeReading(r);
        if (r.visible !== false && r.earned_minutes > 0) {
          const em = r.earned_minutes;
          const label = em < 1 ? `${Math.round(em * 60)} sec` : em < 60 ? `${Math.floor(em)}${em % 1 >= 0.5 ? '½' : ''} min` : hm(Math.round(em));
          setTimeout(() => showReadNote(`+${label} reading time 🌿`), 2500);
        }
      }
      return res.data;
    }).catch(() => null);
  };

  const stopEarly = () => {
    const t = timer;
    setConfirmStop(false);
    setTimer(null);
    holdScreen(false);
    if (t && t.phase === 'focus') {
      const focused = t.planned * 60000 - (t.remaining != null ? t.remaining : Math.max(0, t.endAt - Date.now()));
      saveSession(t, focused, false).then(data => {
        if (data && (data.new_gifts || []).length) setTimeout(() => setGiftNotice(true), 6500);
      });
      say(line('stopped'), pick(stickers.back), 6000);
    }
  };

  const skipBreak = () => {
    setTimer(null);
    say(line('break_over'), null, 5000);
  };

  // ---- the timer reaching its checkpoints ----
  useEffect(() => {
    if (!timer || !running) return;
    const t = timer;
    if (t.phase === 'focus') {
      const cues = t.cues || {};
      const long = t.planned >= 60;
      if (long && !cues.q1 && progress >= 0.25) {
        setTimer({ ...t, cues: { ...cues, q1: true } });
        say(line('now_and_then'));
      } else if (!cues.half && progress >= 0.5) {
        setTimer({ ...t, cues: { ...cues, q1: true, half: true } });
        say(line('halfway'));
      } else if (long && !cues.q3 && progress >= 0.75) {
        setTimer({ ...t, cues: { ...cues, q1: true, half: true, q3: true } });
        say(line('now_and_then'));
      } else if (!cues.almost && remaining <= 5 * 60000) {
        setTimer({ ...t, cues: { ...cues, q1: true, half: true, q3: true, almost: true } });
        say(line('almost'));
      }
    }
    if (remaining <= 0 && !finishingRef.current) {
      finishingRef.current = true;
      if (t.phase === 'focus') finishFocus(t);
      else finishBreak();
    }
  }, [now]);

  const finishFocus = (t) => {
    chime();
    holdScreen(false);
    setCelebrate(c => c + 1);
    say(line('done'), pick(stickers.done), 9000);
    saveSession(t, t.planned * 60000, true).then(data => {
      if (!data) return;
      // a gift milestone crossed during the session: said quietly, only now it's over
      if ((data.new_gifts || []).length) setTimeout(() => setGiftNotice(true), 9500);
      else if (data.goal_just_reached) setTimeout(() => say(line('goal'), pick(stickers.done), 8000), 9200);
    });
    // straight into the break
    setRotateIdx(0);
    setTimer({ phase: 'break', planned: breakLen, endAt: Date.now() + breakLen * 60000, remaining: null, cues: {} });
    finishingRef.current = false;
  };

  const finishBreak = () => {
    chime();
    setTimer(null);
    finishingRef.current = false;
    say(line('break_over'), null, 10000);
  };

  // ---- noticing when you leave mid-session ----
  useEffect(() => {
    const onVis = () => {
      if (document.visibilityState === 'hidden') {
        leftAtRef.current = Date.now();
      } else {
        loadReading();     // catch up on reading time (it may have run out while away)
      }
      if (document.visibilityState !== 'hidden' && leftAtRef.current) {
        const away = Date.now() - leftAtRef.current;
        leftAtRef.current = null;
        setNow(Date.now());
        setTimer(t => {
          // for subjects you study somewhere else (like yapping in ChatGPT) he doesn't mind you leaving
          if (t && t.phase === 'focus' && t.remaining == null && t.endAt > Date.now() && away > 20000 && t.noticeAway !== false) {
            say(line('back'), pick(stickers.back), 6000);
            return { ...t, timesLeft: (t.timesLeft || 0) + 1 };
          }
          return t;
        });
        if (timer && timer.phase === 'focus') holdScreen(true);
      }
    };
    document.addEventListener('visibilitychange', onVis);
    return () => document.removeEventListener('visibilitychange', onVis);
  }, [timer, lines, stickers]);

  // came back to a session that already finished while the page was closed
  useEffect(() => {
    if (timer && timer.remaining == null && timer.endAt <= Date.now() && timer.phase === 'focus') {
      finishingRef.current = true;
      finishFocus(timer);
    } else if (timer && timer.remaining == null && timer.endAt <= Date.now() && timer.phase === 'break') {
      setTimer(null);
    }
  }, []);


  // ---- drawing ----
  const R = 92;
  const C = 2 * Math.PI * R;
  const phaseLabel = !timer ? 'ready when you are' : timer.phase === 'break' ? 'break' : timer.remaining != null ? 'paused' : 'focusing';
  const subjectLabel = timer && timer.phase === 'focus' && shownSubjects.length > 1 && timerSubject ? timerSubject.name : '';
  // everything on the timer takes the colour of the subject you're on
  const accent = (timerSubject && timerSubject.color) || '#c9935a';
  const themed = {
    chip: (on) => ({ ...s.chip(on), ...(on ? { background: `linear-gradient(135deg, ${darken(accent)}, ${accent})` } : { border: `1px solid ${alpha(accent, 0.22)}`, color: accent }) }),
    startBtn: { ...s.startBtn, background: `linear-gradient(135deg, ${darken(accent)}, ${accent})`, boxShadow: `0 6px 18px ${alpha(accent, 0.25)}` },
    ghostBtn: { ...s.ghostBtn, border: `1px solid ${alpha(accent, 0.3)}`, color: accent },
  };
  const subjStats = (stats?.per_subject || {})[(timerSubject || {}).key] || null;
  const goal = (subjStats ? subjStats.goal_minutes : stats?.goal_minutes) || 120;
  const today = (subjStats ? subjStats.today_minutes : stats?.today_minutes) || 0;
  // time together and Caleb's gifts are counted for the subject you're on
  const totalMin = (subjStats ? subjStats.total_minutes : stats?.total_minutes) || 0;
  const nextGift = subjStats ? subjStats.next_gift_hours : stats?.next_gift_hours;
  const prevGift = (subjStats ? subjStats.prev_gift_hours : stats?.prev_gift_hours) || 0;
  const giftPct = nextGift ? Math.min(100, ((totalMin / 60 - prevGift) / (nextGift - prevGift)) * 100) : 100;

  return (
    <div style={s.page}>
      <link href="https://fonts.googleapis.com/css2?family=Cormorant+Garamond:ital,wght@0,300;0,400;1,300;1,400&family=DM+Sans:wght@300;400;500&display=swap" rel="stylesheet" />
      <style>{`
        .st-breathe { animation: breathe 5s ease-in-out infinite; transform-origin: 50% 90%; }
        @keyframes breathe { 0%,100% { transform: scale(1); } 50% { transform: scale(1.025); } }
        .st-bubble { animation: bub .35s ease; }
        @keyframes bub { from { opacity: 0; transform: translate(-50%, 6px); } to { opacity: 1; transform: translate(-50%, 0); } }
        .st-heart { position: absolute; bottom: 30%; color: #e0788f; font-size: 20px; pointer-events: none; animation: strise 1.6s ease-out forwards; }
        @keyframes strise { from { opacity: 0; transform: translate(0,0) scale(.6); } 15% { opacity: 1; } to { opacity: 0; transform: translate(var(--dx), -150px) scale(1.2); } }
        .st-btn:focus-visible, .st-chip:focus-visible { outline: 2px solid #c9935a; outline-offset: 2px; }
        .st-readnote { animation: readnote .6s ease; }
        @keyframes readnote { from { opacity: 0; transform: translateY(4px); } to { opacity: 1; transform: translateY(0); } }
        .st-readpanel { animation: readpanel .45s ease; }
        @keyframes readpanel { from { opacity: 0; transform: translateY(-6px); } to { opacity: 1; transform: translateY(0); } }
        .st-readdot { animation: readdot 2.4s ease-in-out infinite; }
        @keyframes readdot { 0%,100% { opacity: .35; } 50% { opacity: 1; } }
        @media (prefers-reduced-motion: reduce) { .st-readnote, .st-readdot, .st-readpanel { animation: none; } }
        @media (prefers-reduced-motion: reduce) { .st-breathe, .st-bubble, .st-heart { animation: none; } .st-sticker { transition: none !important; } .st-heart { display: none; } }
      `}</style>

      <header style={s.header}>
        <button style={s.circle} onClick={() => navigate('/companions')} aria-label="back">←</button>
        <div style={s.title}>study with caleb</div>
        <button style={s.circle} onClick={() => navigate('/study/keepsakes')} title="From Caleb" aria-label="From Caleb">
          <GiftIcon />
          {giftNotice && <span style={s.dot} aria-hidden="true" />}
        </button>
        <button style={s.circle} onClick={() => navigate('/study/settings')} title="stickers & messages" aria-label="study settings">
          <GearIcon />
        </button>
      </header>

      <main style={s.main}>
        {/* HIM */}
        <div style={s.scene}>
          {bubble && <div className="st-bubble" style={s.bubble}>{bubble}</div>}
          <div className={running && timer?.phase === 'focus' && !momentSticker ? 'st-breathe' : ''}>
            <img
              className="st-sticker"
              src={displayed}
              onError={e => { if (!e.currentTarget.src.endsWith(FALLBACK)) e.currentTarget.src = FALLBACK; }}
              alt=""
              style={{
                ...s.sticker,
                opacity: visible ? 1 : 0,
                transition: `opacity ${visible ? FADE_IN_MS : FADE_OUT_MS}ms ease-in-out`,
              }}
            />
          </div>
          {celebrate > 0 && Array.from({ length: 8 }, (_, i) => (
            <span key={`${celebrate}-${i}`} className="st-heart"
              style={{ left: `${20 + i * 8}%`, animationDelay: `${i * 90}ms`, '--dx': `${(i % 2 ? 1 : -1) * (10 + i * 5)}px` }}>♥</span>
          ))}
        </div>


        {/* TIMER */}
        <div style={s.ringWrap}>
          <svg width="220" height="220" viewBox="0 0 220 220" style={{ display: 'block' }} aria-hidden="true">
            <circle cx="110" cy="110" r={R} fill="none" stroke={alpha(accent, 0.12)} strokeWidth="8" />
            <circle
              cx="110" cy="110" r={R} fill="none"
              stroke={timer?.phase === 'break' ? '#8fb8a0' : accent} strokeWidth="8" strokeLinecap="round"
              strokeDasharray={C} strokeDashoffset={C * (1 - Math.min(1, Math.max(0, progress)))}
              transform="rotate(-90 110 110)" style={{ transition: 'stroke-dashoffset .5s linear' }}
            />
          </svg>
          <div style={s.ringCenter}>
            <div style={s.time}>{fmt(timer ? remaining : focusLen * 60000)}</div>
            <div style={s.phase}>{phaseLabel}</div>
            {subjectLabel && <div style={{ ...s.subjectTag, color: timerSubject.color }}>{subjectLabel}</div>}
          </div>
        </div>

        {/* CONTROLS */}
        {!timer ? (
          <div style={s.controls}>
            {shownSubjects.length > 1 && (
              <label style={s.subjectPick}>
                <span style={{ ...s.dotSmall, background: accent }} />
                <select style={{ ...s.subjectSelect, borderColor: alpha(accent, 0.35) }} value={subject?.key || ''}
                  onChange={e => pickSubject(e.target.value)} aria-label="subject">
                  {shownSubjects.map(x => (
                    <option key={x.key} value={x.key}>{x.name}</option>
                  ))}
                </select>
              </label>
            )}
            <div style={s.optionRow}>
              <span style={s.optionLabel}>focus</span>
              {FOCUS_OPTIONS.map(o => (
                <button key={o.min} className="st-chip" style={themed.chip(focusLen === o.min)} onClick={() => setFocusLen(o.min)}>{o.label}</button>
              ))}
            </div>
            <div style={s.optionRow}>
              <span style={s.optionLabel}>break</span>
              {BREAK_OPTIONS.map(m => (
                <button key={m} className="st-chip" style={themed.chip(breakLen === m)} onClick={() => setBreakLen(m)}>{m}m</button>
              ))}
            </div>
            <button className="st-btn" style={themed.startBtn} onClick={start}>start studying</button>
          </div>
        ) : (
          <div style={s.controls}>
            {confirmStop ? (
              <div style={s.row}>
                <span style={s.muted}>stop now? the minutes you did still count.</span>
                <button className="st-btn" style={s.dangerBtn} onClick={stopEarly}>stop</button>
                <button className="st-btn" style={s.quietBtn} onClick={() => setConfirmStop(false)}>keep going</button>
              </div>
            ) : (
              <div style={s.row}>
                {timer.phase === 'focus' && (timer.remaining != null
                  ? <button className="st-btn" style={themed.ghostBtn} onClick={resume}>resume</button>
                  : <button className="st-btn" style={themed.ghostBtn} onClick={pause}>pause</button>)}
                {timer.phase === 'focus'
                  ? <button className="st-btn" style={s.quietBtn} onClick={() => setConfirmStop(true)}>stop early</button>
                  : <button className="st-btn" style={themed.ghostBtn} onClick={skipBreak}>skip break</button>}
              </div>
            )}
          </div>
        )}

        {/* SOMETHING FROM CALEB IS WAITING — only after a session, never during */}
        {giftNotice && !(timer && timer.phase === 'focus') && (
          <button className="st-btn" style={s.giftNotice} onClick={() => navigate('/study/keepsakes')}>
            something from Caleb is waiting ♡
          </button>
        )}

        {/* QUIET FOOTER: today, and time together */}
        <section style={s.footer}>
          <div style={s.footRow}>
            <span>{hm(today)} of {hm(goal)} today{shownSubjects.length > 1 && timerSubject ? ` · ${timerSubject.name}` : ''}</span>
          </div>
          <div style={s.thinBar}><div style={{ ...s.thinFill, background: `linear-gradient(90deg, ${darken(accent)}, ${accent})`, width: `${Math.min(100, (today / goal) * 100)}%` }} /></div>

          <div style={{ ...s.footRow, marginTop: '18px' }}>
            <span style={s.together}><HeartIcon /> {hm(totalMin)} together{shownSubjects.length > 1 && timerSubject ? <span style={s.togetherSub}>· {timerSubject.name}</span> : null}</span>
          </div>
          {nextGift && (
            <>
              <div style={s.thinBar}><div style={{ ...s.thinFill, ...s.giftFill, width: `${Math.max(0, giftPct)}%` }} /></div>
              <div style={s.footHint}>something from Caleb at {nextGift}h{shownSubjects.length > 1 && timerSubject ? ` of ${timerSubject.name}` : ''}</div>
            </>
          )}

          {/* READING TIME — your bank, earned by studying (1 minute = 1 minute) */}
          {reading && reading.visible && (
            <>
              <div style={{ ...s.footRow, marginTop: '18px' }}>
                <span style={s.reading}><span style={s.readIcon}><BookIcon /></span> <span style={{ fontVariantNumeric: 'tabular-nums' }}>{readActive ? hmsLeft(readLeftMs) : hmSec(readLeftMs / 1000)}</span> to read</span>
                <button
                  className="st-btn"
                  style={s.readToggle(readOpen || readActive)}
                  onClick={() => setReadOpen(o => !o)}
                  aria-expanded={readOpen}
                  aria-label={readOpen ? 'hide reading timer' : 'open reading timer'}
                >
                  {readActive ? <><span className="st-readdot" style={s.readDot} /> reading</> : readOpen ? 'close' : 'read'}
                </button>
              </div>
              <div style={s.thinBar}><div style={{ ...s.thinFill, width: `${readActive && readStartMs ? (readLeftMs / readStartMs) * 100 : readBalanceMs > 0 ? 100 : 0}%` }} /></div>
              {readNote && <div className="st-readnote" style={s.readNote}>{readNote}</div>}

              {/* the reading timer unfolds right here, under the bank */}
              {(readOpen || readActive) && (
                <div className="st-readpanel" style={s.readPanel}>
                  <div style={s.readRingWrap}>
                    <svg width="150" height="150" viewBox="0 0 150 150" style={{ display: 'block' }} aria-hidden="true">
                      <circle cx="75" cy="75" r={RR} fill="none" stroke="rgba(201,147,90,0.1)" strokeWidth="6" />
                      <circle
                        cx="75" cy="75" r={RR} fill="none" stroke="#c9935a" strokeWidth="6" strokeLinecap="round"
                        strokeDasharray={RC}
                        strokeDashoffset={RC * (1 - (readActive && readStartMs ? readLeftMs / readStartMs : readBalanceMs > 0 ? 1 : 0))}
                        transform="rotate(-90 75 75)" style={{ transition: 'stroke-dashoffset .5s linear' }}
                      />
                    </svg>
                    <div style={s.ringCenter}>
                      <div style={s.readTime}>{clock(readSessionMs)}</div>
                      <div style={s.phase}>{readActive ? 'reading' : 'this session'}</div>
                    </div>
                  </div>
                  {(readActive || (readBalanceMs > 0 && !(timer && timer.phase === 'focus'))) && (
                    <label style={s.readPickRow}>
                      <span style={s.readPickLabel}>{readActive ? 'reading' : 'what'}</span>
                      <select style={s.readPick} aria-label="what you're reading"
                        value={readActive ? activeItemValue : (readItems.some(x => `${x.kind}:${x.id}` === readItem) ? readItem : '')}
                        onChange={e => pickReadItem(e.target.value)}>
                        <option value="">something else</option>
                        {readItems.map(x => <option key={`${x.kind}:${x.id}`} value={`${x.kind}:${x.id}`}>{x.title} · {x.note}</option>)}
                        {readActive && reading.active.item_kind && !readItems.some(x => `${x.kind}:${x.id}` === activeItemValue) && (
                          <option value={activeItemValue}>{reading.active.item_title}</option>
                        )}
                      </select>
                    </label>
                  )}
                  <div style={{ marginTop: '12px' }}>
                    {readActive ? (
                      <button className="st-btn" style={s.readStop} disabled={readBusy} onClick={() => stopReading('stopped')}>stop reading</button>
                    ) : timer && timer.phase === 'focus' ? (
                      <div style={s.footHint}>yours to spend once this session's done</div>
                    ) : readBalanceMs > 0 ? (
                      <button className="st-btn" style={s.readStart} disabled={readBusy} onClick={startReading}>start reading</button>
                    ) : (
                      <div style={s.footHint}>every minute you study becomes a minute to read</div>
                    )}
                  </div>
                </div>
              )}
            </>
          )}

          <button style={s.statsLink} onClick={() => navigate('/study/stats')}>statistics →</button>
        </section>
      </main>
    </div>
  );
}

const s = {
  // its own scrolling area, like your other pages (the site stops the page itself from scrolling)
  page: { position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, overflowY: 'auto', WebkitOverflowScrolling: 'touch', background: '#0e0b0f', color: '#e8ddd0', fontFamily: "'DM Sans', sans-serif", fontWeight: 300 },
  header: {
    position: 'sticky', top: 0, zIndex: 10, padding: '18px 20px', display: 'flex', alignItems: 'center', gap: '10px',
    background: 'linear-gradient(180deg, #120f18 0%, #0e0b0f 100%)', borderBottom: '1px solid rgba(201,147,90,0.06)',
  },
  circle: {
    width: '36px', height: '36px', borderRadius: '50%', border: '1px solid rgba(201,147,90,0.15)', background: 'transparent',
    color: '#c9935a', fontSize: '15px', cursor: 'pointer', flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 0,
    position: 'relative',
  },
  title: { flex: 1, minWidth: 0, fontFamily: "'Cormorant Garamond', serif", fontSize: '21px', fontStyle: 'italic', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' },
  main: { maxWidth: '520px', margin: '0 auto', padding: '10px 20px 80px', display: 'flex', flexDirection: 'column', alignItems: 'center', boxSizing: 'border-box' },
  scene: { position: 'relative', width: '100%', display: 'flex', justifyContent: 'center', paddingTop: '56px', minHeight: '262px' },
  sticker: { width: '230px', maxWidth: '62vw', height: 'auto', display: 'block', filter: 'drop-shadow(0 10px 22px rgba(0,0,0,0.5))' },
  bubble: {
    position: 'absolute', top: 0, left: '50%', transform: 'translateX(-50%)', maxWidth: '86%',
    background: '#1e1825', border: '1px solid rgba(201,147,90,0.25)', borderRadius: '16px', padding: '9px 14px',
    fontSize: '13.5px', lineHeight: 1.45, textAlign: 'center', zIndex: 2,
  },
  hearBtn: { marginTop: '6px', background: 'rgba(201,147,90,0.14)', border: 'none', color: '#c9935a', borderRadius: '16px', padding: '6px 14px', fontSize: '12px', cursor: 'pointer', fontFamily: "'DM Sans', sans-serif" },
  ringWrap: { position: 'relative', marginTop: '8px', width: '220px', height: '220px' },
  ringCenter: { position: 'absolute', inset: 0, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center' },
  time: { fontSize: '44px', fontWeight: 400, letterSpacing: '-0.02em', fontVariantNumeric: 'tabular-nums' },
  phase: { fontSize: '12px', color: '#8a7466', marginTop: '2px', letterSpacing: '0.04em' },
  controls: { width: '100%', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '12px', marginTop: '18px' },
  optionRow: { display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap', justifyContent: 'center' },
  optionLabel: { fontSize: '11px', letterSpacing: '0.1em', textTransform: 'uppercase', color: '#6d5d4e', width: '48px' },
  chip: (on) => ({
    padding: '7px 16px', borderRadius: '16px', fontSize: '13px', cursor: 'pointer', fontFamily: "'DM Sans', sans-serif",
    border: on ? 'none' : '1px solid rgba(201,147,90,0.18)',
    background: on ? 'linear-gradient(135deg, #8a5f35, #c9935a)' : 'transparent', color: on ? '#0e0b0f' : '#c9a57a',
  }),
  startBtn: {
    marginTop: '4px', padding: '14px 36px', borderRadius: '24px', border: 'none', cursor: 'pointer',
    background: 'linear-gradient(135deg, #8a5f35, #c9935a)', color: '#0e0b0f', fontSize: '15px', fontWeight: 500,
    fontFamily: "'DM Sans', sans-serif", boxShadow: '0 6px 18px rgba(201,147,90,0.25)',
  },
  row: { display: 'flex', alignItems: 'center', gap: '10px', flexWrap: 'wrap', justifyContent: 'center' },
  ghostBtn: { padding: '10px 22px', borderRadius: '20px', border: '1px solid rgba(201,147,90,0.25)', background: 'transparent', color: '#c9935a', fontSize: '13px', cursor: 'pointer', fontFamily: "'DM Sans', sans-serif" },
  quietBtn: { padding: '10px 14px', border: 'none', background: 'transparent', color: '#6d5d4e', fontSize: '13px', cursor: 'pointer', fontFamily: "'DM Sans', sans-serif" },
  dangerBtn: { padding: '10px 18px', borderRadius: '20px', border: 'none', background: '#b04a4a', color: '#f4e6e6', fontSize: '13px', cursor: 'pointer', fontFamily: "'DM Sans', sans-serif" },
  muted: { fontSize: '12.5px', color: '#8a7466', textAlign: 'center' },
  dot: { position: 'absolute', top: '6px', right: '6px', width: '7px', height: '7px', borderRadius: '50%', background: '#e0a0b0' },
  giftNotice: {
    marginTop: '26px', background: 'rgba(201,147,90,0.08)', border: '1px solid rgba(201,147,90,0.22)', borderRadius: '16px',
    padding: '10px 18px', color: '#e8ddd0', fontSize: '13.5px', fontFamily: "'Cormorant Garamond', serif", fontStyle: 'italic',
    cursor: 'pointer', letterSpacing: '0.01em',
  },
  footer: { width: '100%', maxWidth: '360px', marginTop: '40px' },
  // study subjects
  togetherSub: { fontFamily: "'DM Sans', sans-serif", fontSize: '12px', color: '#8a7466', marginLeft: '4px' },
  subjectPick: { display: 'inline-flex', alignItems: 'center', gap: '9px' },
  subjectSelect: {
    background: '#120f18', border: '1px solid rgba(201,147,90,0.3)', borderRadius: '14px', padding: '8px 12px',
    color: '#e8ddd0', fontSize: '13.5px', fontFamily: "'DM Sans', sans-serif", outline: 'none', cursor: 'pointer', maxWidth: '78vw',
    minWidth: '210px', textAlign: 'center', textAlignLast: 'center',
  },
  subjectChip: (on, color) => ({
    display: 'inline-flex', alignItems: 'center', gap: '7px', padding: '7px 14px', borderRadius: '16px', fontSize: '13px', cursor: 'pointer',
    fontFamily: "'DM Sans', sans-serif", border: `1px solid ${on ? color : 'rgba(201,147,90,0.18)'}`,
    background: on ? 'rgba(201,147,90,0.08)' : 'transparent', color: on ? '#e8ddd0' : '#a8927f',
  }),
  dotSmall: { width: '8px', height: '8px', borderRadius: '50%', display: 'inline-block', flexShrink: 0 },
  rateTag: { fontSize: '11px', color: '#8a7466' },
  subjectTag: { fontSize: '11.5px', marginTop: '3px', letterSpacing: '0.03em' },
  subjectSplit: { display: 'flex', flexWrap: 'wrap', gap: '6px 14px', marginTop: '6px', fontSize: '11.5px', color: '#8a7466' },
  subjectSplitItem: { display: 'inline-flex', alignItems: 'center', gap: '6px' },
  // reading: the same row as "together", in the break ring's green
  reading: { display: 'inline-flex', alignItems: 'center', gap: '7px', fontFamily: "'Cormorant Garamond', serif", fontSize: '18px', color: '#e8ddd0' },
  readIcon: { color: '#c9935a', display: 'inline-flex' },
  readToggle: (on) => ({
    display: 'inline-flex', alignItems: 'center', gap: '6px', padding: '4px 12px', borderRadius: '14px', cursor: 'pointer',
    border: `1px solid ${on ? 'rgba(201,147,90,0.4)' : 'rgba(201,147,90,0.2)'}`, background: on ? 'rgba(201,147,90,0.08)' : 'transparent',
    color: '#c9935a', fontSize: '11.5px', fontFamily: "'DM Sans', sans-serif", letterSpacing: '0.03em',
  }),
  readDot: { width: '6px', height: '6px', borderRadius: '50%', background: '#c9935a', display: 'inline-block' },
  readNote: { fontSize: '11.5px', color: '#8a7466', marginTop: '7px', fontStyle: 'italic' },
  readPanel: { display: 'flex', flexDirection: 'column', alignItems: 'center', marginTop: '18px', paddingBottom: '4px' },
  readRingWrap: { position: 'relative', width: '150px', height: '150px' },
  readTime: { fontSize: '24px', fontWeight: 400, letterSpacing: '0.01em', fontVariantNumeric: 'tabular-nums' },
  readPickRow: { display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px', marginTop: '12px' },
  readPickLabel: { fontSize: '10.5px', letterSpacing: '0.1em', textTransform: 'uppercase', color: '#6d5d4e' },
  readPick: { background: '#120f18', border: '1px solid rgba(201,147,90,0.22)', borderRadius: '12px', padding: '7px 10px', color: '#e8ddd0', fontSize: '12.5px', fontFamily: "'DM Sans', sans-serif", outline: 'none', maxWidth: '230px', textAlign: 'center', textAlignLast: 'center' },
  readStart: { padding: '9px 24px', borderRadius: '20px', border: '1px solid rgba(201,147,90,0.25)', background: 'transparent', color: '#c9935a', fontSize: '13px', cursor: 'pointer', fontFamily: "'DM Sans', sans-serif" },
  readStop: { padding: '9px 24px', borderRadius: '20px', border: '1px solid rgba(201,147,90,0.25)', background: 'transparent', color: '#c9935a', fontSize: '13px', cursor: 'pointer', fontFamily: "'DM Sans', sans-serif" },
  footRow: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', fontSize: '12.5px', color: '#8a7466' },
  together: { display: 'inline-flex', alignItems: 'center', gap: '7px', fontFamily: "'Cormorant Garamond', serif", fontSize: '18px', color: '#e8ddd0' },
  thinBar: { height: '3px', borderRadius: '2px', background: 'rgba(201,147,90,0.1)', marginTop: '8px', overflow: 'hidden' },
  thinFill: { height: '100%', borderRadius: '2px', background: 'linear-gradient(90deg, #8a5f35, #c9935a)', transition: 'width .4s ease' },
  giftFill: { background: 'linear-gradient(90deg, #7a5a7e, #c9a0b0)' },
  footHint: { fontSize: '11.5px', color: '#6d5d4e', marginTop: '7px', fontStyle: 'italic' },
  statsLink: { display: 'block', margin: '26px auto 0', background: 'none', border: 'none', color: '#6d5d4e', fontSize: '12px', cursor: 'pointer', fontFamily: "'DM Sans', sans-serif" },
};
